import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

export interface MiningLogSummary {
  logId: string;
  userId: string;
  landId: string;
  landType: string;
  blocksMined: number;
  itemsMinted: Array<{ itemName: string; quantity: number }>;
  createdAt: string;
}

interface UseMiningLogReturn {
  entries: MiningLogSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useMiningLog(): UseMiningLogReturn {
  const [entries, setEntries] = useState<MiningLogSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const fetchIdRef = useRef(0);

  const fetchLog = useCallback(async (newOffset = 0, append = false) => {
    const id = ++fetchIdRef.current;
    try {
      setError(null);
      setLoading(true);
      const params: Record<string, string> = {
        limit: String(limit),
        offset: String(newOffset),
      };

      const { data } = await api.get<{ entries: MiningLogSummary[]; total: number }>(
        '/arches/mining/log',
        { params },
      );
      if (fetchIdRef.current !== id) return;
      if (append) {
        setEntries((prev) => [...prev, ...data.entries]);
      } else {
        setEntries(data.entries);
      }
      setTotal(data.total);
      setOffset(newOffset);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load mining log');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchLog(0);
  }, [fetchLog]);

  const refresh = useCallback(() => fetchLog(0), [fetchLog]);
  const loadMore = useCallback(() => {
    if (offset + limit < total) {
      fetchLog(offset + limit, true);
    }
  }, [fetchLog, offset, total]);

  return {
    entries,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
  };
}
